import axios, { AxiosInstance, AxiosRequestConfig } from 'axios'
import { defaultAxiosConfig, loginWithSsoUrl, setCookieUrl } from 'app/constants'
import Designate from './Designate'
import OpenCloud from './opencloud/OpenCloud'
import Glu from './opencloud/Glu'
import Cinder from './Cinder'
import Glance from './Glance'
import Keystone from './Keystone'
import Murano from './Murano'
import Neutron from './Neutron'
import Nova from './Nova'
import ResMgr from './ResMgr'
import Clemency from 'api-client/Clemency'
import Helm from './Helm'
import Hagrid from './Hagrid'
import HaMgr from './HaMgr'
import Heat from './Heat'
import Placement from './Placement'
import Kubernetes from './Kubernetes'
import Capi from './Capi'
import Mors from './Mors'
import VMHA from './VMHA'

import { getDomainName, normalizeResponse } from 'api-client/helpers'
import { hasPathStr, pathStr } from 'utils/fp'
import { prop, pick, has, cond, T, identity, when, mergeDeepLeft } from 'ramda'
import { isPlainObject, pathJoin } from 'utils/misc'
import {
  DDUHealth,
  IRawRequestGetParams,
  IRawRequestPostParams,
  IBasicRequestGetParams,
  IBasicRequestPostParams,
  IBasicRequestDeleteParams,
} from './model'
import ApiService from 'api-client/ApiService'
import Bugsnag from 'utils/bugsnag'
import { someAsync } from 'utils/async'
import PreferenceStore from './PreferenceStore'
import config from 'app-config'
import store from 'app/store'
import { sessionStoreKey, sessionActions } from 'core/session/sessionReducers'
import { cacheActions } from 'core/caching/cacheReducers'
import { loginUrl } from 'app/constants'
import Octavia from './Octavia'
import { getSSORedirectUrl, isDevelopmentMode } from 'core/utils/helpers'
import { preferencesActions } from 'core/session/preferencesReducers'
import AppCatalog from './AppCatalog'

// Cannot import this from tracking.js due to circular dependency
const trackEvent = (name, body = {}) => {
  // @ts-ignore
  if (window.amplitude) {
    const state = store.getState()
    const fqdn = window.location.origin
    const url = window.location.href
    const version = state?.session?.features?.releaseVersion
    // @ts-ignore
    window.amplitude.track(name, {
      ...body,
      fqdn,
      url,
      appVersion: version,
    })
  }
}

const addApiRequestMetadata = (apiClass, requestMethod) => {
  return { apiClassMetadata: { apiClass, requestMethod } }
}
const requestKeysToTrack = [
  'readyState',
  'response',
  'responseText',
  'responseType',
  'responseURL',
  'responseXML',
  'status',
  'statusText',
  'timeout',
  'withCredentials',
]
const formatResponseRequestPayload = (data = {}) => pick(requestKeysToTrack, data)

const formatResponseConfigPayload = (cfg: AxiosRequestConfig = {}) => ({
  url: cfg?.url,
  method: cfg?.method,
  data: typeof cfg?.data === 'string' ? JSON.parse(cfg?.data) : cfg?.data,
  headers: cfg?.headers,
})

interface ApiClientOptions {
  [key: string]: any

  keystoneEndpoint: string
}

export const apiClassesByApiName = {
  designate: Designate,
  opencloud: OpenCloud,
  glu: Glu,
  cinder: Cinder,
  glance: Glance,
  keystone: Keystone,
  neutron: Neutron,
  octavia: Octavia,
  appcatalog: AppCatalog,
  nova: Nova,
  murano: Murano,
  resMgr: ResMgr,
  clemency: Clemency,
  helm: Helm,
  hagrid: Hagrid,
  preferenceStore: PreferenceStore,
  haMgr: HaMgr,
  heat: Heat,
  kaapi: Kubernetes,
  placement: Placement,
  capi: Capi,
  mors: Mors,
  vmha: VMHA,
}

class ApiClient {
  public options: ApiClientOptions
  private unauthorizedErrorCount: number = 0
  private readonly MAX_UNAUTHORIZED_ERRORS = 10
  private hasSessionExpiredEventDispatched: boolean = false // Flag to track if event was dispatched
  public designate: Designate
  public opencloud: OpenCloud
  public glu: Glu
  public cinder: Cinder
  public glance: Glance
  public keystone: Keystone
  public neutron: Neutron
  public octavia: Octavia
  public appcatalog: AppCatalog
  public nova: Nova
  public murano: Murano
  public resMgr: ResMgr
  public clemency: Clemency
  public helm: Helm
  public hagrid: Hagrid
  public preferenceStore: PreferenceStore
  public haMgr: HaMgr
  public heat: Heat
  public kubernetes: Kubernetes
  public capi: Capi
  public mors: Mors
  public placement: Placement
  public vmha: VMHA
  public catalog = {}
  unscopedToken = null
  scopedToken = null
  activeProjectId = null
  serviceCatalog = null
  endpoints = null

  private readonly axiosInstance: AxiosInstance
  private static instance: ApiClient

  static init(options: ApiClientOptions) {
    if (!ApiClient.instance) {
      ApiClient.instance = new ApiClient(options)
    }
    return ApiClient.instance
  }

  static getInstance() {
    if (!ApiClient.instance) {
      console.warn(
        'ApiClient instance has not been initialized, please call ApiClient.init to instantiate it',
      )
    }
    return ApiClient.instance || ({} as ApiClient)
  }

  static hydrate(state) {
    const options = {
      keystoneEndpoint: state.keystoneEndpoint,
    }
    const client = new ApiClient(options)
    client.catalog = state.catalog
    return client
  }

  static async refreshApiEndpoints(instance = ApiClient.getInstance()) {
    await someAsync([
      instance.opencloud.initialize(),
      instance.glu.initialize(),
      instance.keystone.initialize(),
      instance.cinder.initialize(),
      instance.glance.initialize(),
      instance.neutron.initialize(),
      instance.octavia.initialize(),
      instance.appcatalog.initialize(),
      instance.nova.initialize(),
      instance.murano.initialize(),
      instance.resMgr.initialize(),
      instance.clemency.initialize(),
      instance.helm.initialize(),
      instance.hagrid.initialize(),
      instance.preferenceStore.initialize(),
      instance.haMgr.initialize(),
      instance.heat.initialize(),
      instance.kubernetes.initialize(),
      instance.placement.initialize(),
      instance.capi.initialize(),
      instance.mors.initialize(),
      instance.vmha.initialize(),
    ])
  }

  // @deprecated use the store directly
  get activeRegion(): string {
    const {
      session: { activeRegion },
    } = store.getState()
    return activeRegion
  }

  // Get JWT token from the store
  get jwtToken() {
    const state = store.getState()
    const { jwtToken } = state[sessionStoreKey]
    return jwtToken
  }

  apiServices = {}

  constructor(options: ApiClientOptions) {
    this.options = options
    if (!options.keystoneEndpoint) {
      console.warn('keystoneEndpoint required')
    }
    const getResponseError: any = (cond as any)([
      [hasPathStr('response.data.error'), pathStr('response.data.error')],
      [hasPathStr('response.data.message'), pathStr('response.data.message')],
      [has('error'), when<any, string>(isPlainObject, prop('error'))],
      [T, identity],
    ])
    const getErrorText = (err) => {
      if (err.message) {
        return err.message
      }
      if (typeof err === 'object') {
        return JSON.stringify(err)
      }
      return err.toString()
    }

    this.axiosInstance = axios.create({ ...defaultAxiosConfig, ...(options.axios || {}) })

    // Proxy mode: when VITE_DU_TARGET is set, rewrite absolute DU URLs to
    // relative paths so requests go through the Vite dev-server proxy (avoids CORS).
    const duTarget = process.env.VITE_DU_TARGET
    if (isDevelopmentMode() && duTarget) {
      try {
        const duOrigin = new URL(duTarget).origin
        this.axiosInstance.interceptors.request.use((reqConfig) => {
          if (reqConfig.url?.startsWith(duOrigin)) {
            reqConfig.url = reqConfig.url.slice(duOrigin.length) || '/'
          }
          return reqConfig
        })
      } catch (error) {
        // invalid VITE_DU_TARGET — skip interceptor
        console.warn(`Invalid VITE_DU_TARGET format: ${duTarget}`, error)
      }
    }

    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Reset counter on successful response
        this.unauthorizedErrorCount = 0
        return response
      },
      (error) => {
        if (error?.response?.status === 401) {
          this.unauthorizedErrorCount++
          if (this.unauthorizedErrorCount >= this.MAX_UNAUTHORIZED_ERRORS) {
            this.handleSessionExpiration()
          }
        } else {
          // Reset counter for non-401 errors
          this.unauthorizedErrorCount = 0
        }
        return Promise.reject(error)
      },
    )

    this.axiosInstance.interceptors.response.use(
      (response) => response,
      async (error) => {
        const response = error?.response

        const responseError = getResponseError(error)
        const errorMessage = getErrorText(responseError)

        const errStack = error?.stack || error?.stackTrace
        const responseConfig = response?.config
        const apiClassMetadata = responseConfig?.apiClassMetadata || {}

        const responseData = response?.data
        const responseStatus = response?.status
        const responseStatusText = response?.statusText

        const { apiClass, requestMethod } = apiClassMetadata
        const metaData = apiClassesByApiName?.[apiClass]?.apiMethodsMetadata?.find(
          (metadata) => metadata.name === requestMethod,
        )
        // we have to define this here because its used inside the toolbar,
        // and not all selectors are defined when that file is imported
        // (reselct exception thrown if we import the notification selector)

        const isClusterError = !!metaData?.error?.isClusterError

        const errResponseJson = {
          config: formatResponseConfigPayload(responseConfig),
          data: responseData,
          status: responseStatus,
          statusText: responseStatusText,
        }

        // If data contains user password, remove it
        if (
          responseConfig?.data?.includes &&
          responseConfig?.data?.includes('{"methods":["password"]')
        ) {
          const dataObj = JSON.parse(responseConfig?.data)
          if (dataObj?.auth?.identity?.password?.user?.password) {
            dataObj.auth.identity.password.user.password = 'SENSITIVE_DATA_REMOVED'
            responseConfig.data = JSON.stringify(dataObj)
          }
        }

        // Track only events that were actions triggered by the user, so Non-GET requests
        if (responseConfig?.method && responseConfig?.method !== 'get') {
          trackEvent(
            `${responseConfig?.method?.toUpperCase()} Request Failed - ${responseConfig
              ?.apiClassMetadata?.requestMethod || 'Not Defined'}`,
            {
              url: responseConfig?.url,
              message: errorMessage,
              name: error?.name,
              apiClassMetadata: responseConfig?.apiClassMetadata,
              code: error?.code,
            },
          )
        }

        Bugsnag.notify(new Error(errorMessage), (bugsnagEvent: any) => {
          /*
            update the context of the error to be the same as
            the error message, this way they can be uniquely split apart
          */
          bugsnagEvent.context = errorMessage
          bugsnagEvent.groupingHash = errorMessage

          bugsnagEvent.addMetadata('Response Config', responseConfig)
          bugsnagEvent.addMetadata('Request', formatResponseRequestPayload(response?.request))
          bugsnagEvent.addMetadata('Response Data', responseData)
          bugsnagEvent.addMetadata('Stacktrace', errStack)
        })

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({
          isClusterError,
          apiClassMetadata,
          apiErrorMetadata: metaData?.error || {},
          response: errResponseJson,
          stack: errStack,
          err: responseError,
        })
      },
    )

    // Keystone is used by all the other services so it must be initialized first
    this.opencloud = this.addApiService(new OpenCloud(this))
    this.glu = this.addApiService(new Glu(this))
    this.keystone = this.addApiService(new Keystone(this))
    this.designate = this.addApiService(new Designate(this))
    this.cinder = this.addApiService(new Cinder(this))
    this.glance = this.addApiService(new Glance(this))
    this.neutron = this.addApiService(new Neutron(this))
    this.octavia = this.addApiService(new Octavia(this))
    this.appcatalog = this.addApiService(new AppCatalog(this))
    this.nova = this.addApiService(new Nova(this))
    this.murano = this.addApiService(new Murano(this))
    this.resMgr = this.addApiService(new ResMgr(this))
    this.clemency = this.addApiService(new Clemency(this))
    this.helm = this.addApiService(new Helm(this))
    this.hagrid = this.addApiService(new Hagrid(this))
    this.preferenceStore = this.addApiService(new PreferenceStore(this))
    this.haMgr = this.addApiService(new HaMgr(this))
    this.heat = this.addApiService(new Heat(this))
    this.kubernetes = this.addApiService(new Kubernetes(this))
    this.placement = this.addApiService(new Placement(this))
    this.capi = this.addApiService(new Capi(this))
    this.mors = this.addApiService(new Mors(this))
    this.vmha = this.addApiService(new VMHA(this))
  }

  addApiService = <T extends ApiService>(apiClientInstance: T) => {
    this.apiServices[apiClientInstance.getClassName()] = apiClientInstance
    return apiClientInstance
  }

  serialize = () => {
    return {
      keystoneEndpoint: this.options.keystoneEndpoint,
      unscopedToken: this.unscopedToken,
      scopedToken: this.scopedToken,
      catalog: this.catalog,
      activeProjectId: this.activeProjectId,
    }
  }

  getSystemHealth = async () => {
    const data = await this.basicGet<DDUHealth>({
      url: '/regionstatus',
      endpoint: config.apiHost,
      options: {
        clsName: 'ApiClient',
        mthdName: 'getSystemHealth',
      },
    })
    return data
  }

  getAuthHeaders = ({ scoped = true, useJWTToken = false } = {}) => {
    const token = scoped ? this.scopedToken : this.unscopedToken
    if (!token) {
      console.warn('Auth token not initialized')
      return {}
    }
    // It's not necessary to send both headers but it's easier since we don't
    // need to pass around the url and have conditional logic.
    // Both APIs will ignore any headers they don't understand.
    const headers = useJWTToken
      ? {
          Authorization: `Bearer ${this.jwtToken}`, // Required for Kaapi APIs
        }
      : {
          'X-Auth-Token': token, // required for OpenStack
        }
    return { headers }
  }

  async getEndpoint({ version, endpoint, clsName, mock = false }) {
    if (mock) {
      return config.mockServer
    }

    if (endpoint === undefined) {
      endpoint = await this.apiServices[clsName].getApiEndpoint()
    }

    // Proxy mode: when apiHost is empty, all requests must be relative so they
    // route through the dev-server proxy. Convert any absolute catalog URL
    // (e.g. https://du.example.com/keystone/v3) to a relative path (/keystone/v3).
    if (isDevelopmentMode() && !config.apiHost && endpoint && endpoint.startsWith('http')) {
      try {
        endpoint = new URL(endpoint).pathname
      } catch (error) {
        // keep endpoint as-is if URL parsing fails
        console.warn(`Failed to parse endpoint URL: ${endpoint}`, error)
      }
    }

    if (version !== undefined) {
      endpoint = endpoint
        .replace(/\/v3$/, `/${version}`)
        .replace(/\/v3\//, `/${version}/`)
        .replace(/\/v1$/, `/${version}`)
        .replace(/\/v1\//, `/${version}/`)
    }
    // eslint-disable-next-line no-extra-boolean-cast
    if (!!this.apiServices[clsName]?.scopedEnpointPath) {
      return `${endpoint}/${this.apiServices[clsName].scopedEnpointPath()}`
    }
    return endpoint
  }

  rawGet = async <T>({
    url,
    version,
    endpoint = undefined,
    config = {},
    options: { clsName, mthdName },
  }: IRawRequestGetParams) => {
    endpoint = await this.getEndpoint({ endpoint, version, clsName })
    const axiosConfig = {
      ...config,
      ...addApiRequestMetadata(clsName, mthdName),
    }
    const response = await this.axiosInstance.get<T>(pathJoin(endpoint, url), axiosConfig)
    // ApiCache.instance.cacheItem(clsName, mthdName, response.data)
    return response
  }

  rawPost = async <T>({
    url,
    version,
    data,
    endpoint = undefined,
    config = {},
    options: { clsName, mthdName },
  }: IRawRequestPostParams) => {
    endpoint = await this.getEndpoint({ endpoint, version, clsName })
    const axiosConfig = {
      ...config,
      ...addApiRequestMetadata(clsName, mthdName),
    }
    const response = await this.axiosInstance.post<T>(pathJoin(endpoint, url), data, axiosConfig)
    // ApiCache.instance.cacheItem(clsName, mthdName, response.data)
    return response
  }

  rawPut = async <T>({
    url,
    version,
    data,
    endpoint = undefined,
    config = {},
    options: { clsName, mthdName },
  }: IRawRequestPostParams) => {
    endpoint = await this.getEndpoint({ endpoint, version, clsName })
    const axiosConfig = {
      ...config,
      ...addApiRequestMetadata(clsName, mthdName),
    }
    const response = await this.axiosInstance.put<T>(pathJoin(endpoint, url), data, axiosConfig)
    // ApiCache.instance.cacheItem(clsName, mthdName, response.data)
    return response
  }

  rawPatch = async <T>({
    url,
    version,
    data,
    endpoint = undefined,
    config = {},
    options: { clsName, mthdName },
  }: IRawRequestPostParams) => {
    endpoint = await this.getEndpoint({ endpoint, version, clsName })
    const axiosConfig = {
      ...config,
      ...addApiRequestMetadata(clsName, mthdName),
    }
    const response = await this.axiosInstance.patch<T>(pathJoin(endpoint, url), data, axiosConfig)
    // ApiCache.instance.cacheItem(clsName, mthdName, response.data)
    return response
  }

  rawDelete = async <T>({
    url,
    version,
    endpoint = undefined,
    config = {},
    options: { clsName, mthdName },
  }: IRawRequestGetParams) => {
    endpoint = await this.getEndpoint({ endpoint, version, clsName })
    const axiosConfig = {
      ...config,
      ...addApiRequestMetadata(clsName, mthdName),
    }
    const response = await this.axiosInstance.delete<T>(pathJoin(endpoint, url), axiosConfig)
    // ApiCache.instance.cacheItem(clsName, mthdName, response.data)
    return response
  }

  private handleSessionExpiration() {
    // Only dispatch event if it hasn't been dispatched before
    if (!this.hasSessionExpiredEventDispatched) {
      // Dispatch custom event for session expiration
      const event = new CustomEvent('sessionExpired', {
        detail: {
          message: 'Session Expired. Redirecting to login page...',
        },
      })
      window.dispatchEvent(event)

      // Store current path and user in app store before logging out
      const currentPath = window.location.pathname
      const state = store.getState()
      const { features = {}, username, userDetails } = state[sessionStoreKey]

      store.dispatch(
        preferencesActions?.updatePrefs({
          username,
          key: 'root',
          prefs: { lastVisitedPath: currentPath } as { [key: string]: any },
        }),
      )

      this.hasSessionExpiredEventDispatched = true // Set flag to true after dispatching to prevent triggering multiple events
      this.unauthorizedErrorCount = 0

      if (userDetails?.federated?.length > 0) {
        // If the user is using SSO, redirect to the SSO login URL
        const domain = getDomainName()
        const ssoRedirectUrl = getSSORedirectUrl(domain)
        const originHost = window.location.origin
        const infraHost = `https://${features?.sso_infra_url}`

        if (originHost === infraHost) {
          window.location.href = ssoRedirectUrl
        } else {
          const url = new URL(
            `${infraHost}${setCookieUrl}?redirectUrl=${originHost}${loginWithSsoUrl}&domain=${domain}`,
          )
          setTimeout(() => {
            store.dispatch(sessionActions.destroySession())
            store.dispatch(cacheActions.clearCache())
            window.location.replace(url)
          }, 4000)
          return
        }
      }
      setTimeout(() => {
        store.dispatch(sessionActions.destroySession())
        store.dispatch(cacheActions.clearCache())
      }, 5000)
    }
  }

  basicGet = async <T>({
    url,
    version,
    endpoint = undefined,
    params = undefined,
    options: {
      clsName,
      mthdName,
      extractNestedData = true,
      mock,
      config = {},
      useJWTToken = false,
    },
  }: IBasicRequestGetParams) => {
    endpoint = await this.getEndpoint({ endpoint, version, clsName, mock })
    const response = await this.axiosInstance.get<T>(pathJoin(endpoint, url), {
      params,
      ...mergeDeepLeft(this.getAuthHeaders({ useJWTToken }), {
        ...config,
        ...addApiRequestMetadata(clsName, mthdName),
      }),
    })
    // ApiCache.instance.cacheItem(clsName, mthdName, response.data)
    return normalizeResponse<T>(response, extractNestedData)
  }

  basicPost = async <T>({
    url,
    version,
    endpoint = undefined,
    body = undefined,
    options: { clsName, mthdName, extractNestedData = true, config = {}, useJWTToken = false },
  }: IBasicRequestPostParams) => {
    endpoint = await this.getEndpoint({ endpoint, version, clsName })
    const response = await this.axiosInstance.post<T>(
      pathJoin(endpoint, url),
      body,
      mergeDeepLeft(this.getAuthHeaders({ useJWTToken }), {
        ...config,
        ...addApiRequestMetadata(clsName, mthdName),
      }),
    )
    // ApiCache.instance.cacheItem(clsName, mthdName, response.data)
    return normalizeResponse<T>(response, extractNestedData)
  }

  basicPatch = async <T>({
    url,
    version,
    endpoint = undefined,
    body = undefined,
    options: { clsName, mthdName, extractNestedData = true, config = {}, useJWTToken = false },
  }: IBasicRequestPostParams) => {
    endpoint = await this.getEndpoint({ endpoint, version, clsName })
    const response = await this.axiosInstance.patch<T>(
      pathJoin(endpoint, url),
      body,
      mergeDeepLeft(this.getAuthHeaders({ useJWTToken }), {
        ...config,
        ...addApiRequestMetadata(clsName, mthdName),
      }),
    )
    // ApiCache.instance.cacheItem(clsName, mthdName, response.data)
    return normalizeResponse<T>(response, extractNestedData)
  }

  basicPut = async <T>({
    url,
    version,
    endpoint = undefined,
    body = undefined,
    options: { clsName, mthdName, extractNestedData = true, config = {}, useJWTToken = false },
  }: IBasicRequestPostParams) => {
    endpoint = await this.getEndpoint({ endpoint, version, clsName })
    const response = await this.axiosInstance.put<T>(
      pathJoin(endpoint, url),
      body,
      mergeDeepLeft(this.getAuthHeaders({ useJWTToken }), {
        ...config,
        ...addApiRequestMetadata(clsName, mthdName),
      }),
    )
    // ApiCache.instance.cacheItem(clsName, mthdName, response.data)
    return normalizeResponse<T>(response, extractNestedData)
  }

  basicDelete = async <T>({
    url,
    version,
    endpoint = undefined,
    options: { clsName, mthdName, extractNestedData = true, config = {}, useJWTToken = false },
    data = undefined,
  }: IBasicRequestDeleteParams) => {
    endpoint = await this.getEndpoint({ endpoint, version, clsName })
    const response = await this.axiosInstance.delete<T>(
      pathJoin(endpoint, url),
      mergeDeepLeft(this.getAuthHeaders({ useJWTToken }), {
        ...data,
        ...config,
        ...addApiRequestMetadata(clsName, mthdName),
      }),
    )
    // ApiCache.instance.cacheItem(clsName, mthdName, response.data)
    return normalizeResponse<T>(response, extractNestedData)
  }
}

export default ApiClient
